"use client";

import React, { useState, useEffect } from "react";
import BookingTable from "../common/Table";
import {
  useGetLocations,
  useAddLocation,
  useUpdateLocation,
  useDeleteLocations,
  useGetCountries,
  useGetStates,
  useGetCities,
} from "@/hooks/useCommon";
import {
  X,
  Trash2,
  Edit,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Plus,
  Search,
} from "lucide-react";
import DynamicForm from "./Popup";
import Button from "./Button";
import { toast } from "react-toastify";

interface TableData {
  id: number;
  sno: number;
  area: string;
  zone: string;
  country: string;
  state: string;
  city: string;
  latitude: string;
  longitude: string;
  created_by: string;
  created_at: string;
  country_id: number;
  state_id: number;
  city_id: number;
}

const ManageLocation = () => {
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [activeStatus, setActiveStatus] = useState(true);
  const [inactiveStatus, setInactiveStatus] = useState(false);
  const [currentRow, setCurrentRow] = useState<TableData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // For useMutation, we need to call mutate to refetch data
  const { data: locationDetail, mutate: getLocationsMutate } = useGetLocations();
  const [rawLocationData, setRawLocationData] = useState<any[]>([]);
  const [filteredLocationData, setFilteredLocationData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  
  const { mutate: addLocationMutate } = useAddLocation();
  const { mutate: updateLocationMutate } = useUpdateLocation();
  const { mutate: deleteLocationMutate } = useDeleteLocations();
  
  const { data: countryList } = useGetCountries();
  
  // Separate state data for search filters
  const { data: stateList, refetch: refetchStates } = useGetStates(country);
  const { data: cityList, refetch: refetchCities } = useGetCities(state);

  // Separate state data for forms (modals) - COMPLETELY SEPARATE
  const [formCountry, setFormCountry] = useState("");
  const [formState, setFormState] = useState("");
  const [formCity, setFormCity] = useState("");
  
  const { data: formStateList, refetch: refetchFormStates } = useGetStates(formCountry);
  const { data: formCityList, refetch: refetchFormCities } = useGetCities(formState);

  // Debugging logs
  console.log("Country List:", countryList);
  console.log("Search - State List:", stateList);
  console.log("Search - City List:", cityList);
  console.log("Form - State List:", formStateList);
  console.log("Form - City List:", formCityList);
  console.log("Form Country:", formCountry);
  console.log("Form State:", formState);
  console.log("Form City:", formCity);

  // Function to refetch locations
  const refetchLocations = () => {
    getLocationsMutate({});
  };

  useEffect(() => {
    refetchLocations();
  }, []);

  useEffect(() => {
    if (Array.isArray(locationDetail?.data)) {
      setRawLocationData(locationDetail.data);
      setFilteredLocationData(locationDetail.data);
    } else if (Array.isArray(locationDetail)) {
      setRawLocationData(locationDetail);
      setFilteredLocationData(locationDetail);
    }
  }, [locationDetail]);

  // Effects for search filters
  useEffect(() => {
    if (country) {
      console.log("Refetching search states for country:", country);
      refetchStates();
    } else {
      console.log("No country selected in search, clearing state");
      setState("");
    }
  }, [country]);

  useEffect(() => {
    if (state) {
      console.log("Refetching search cities for state:", state);
      refetchCities();
    } else {
      console.log("No state selected in search, clearing city");
      setCity("");
    }
  }, [state]);

  // Effects for form filters
  useEffect(() => {
    if (formCountry) {
      console.log("Refetching form states for country:", formCountry);
      refetchFormStates();
    } else {
      console.log("No country selected in form, clearing form state");
      setFormState("");
    }
  }, [formCountry]);

  useEffect(() => {
    if (formState) {
      console.log("Refetching form cities for state:", formState);
      refetchFormCities();
    } else {
      console.log("No state selected in form, clearing form city");
      setFormCity("");
    }
  }, [formState]);

  // Reset form fields when modal opens/closes
  useEffect(() => {
    if (isAddModalOpen) {
      // Reset form fields when add modal opens
      setFormCountry("");
      setFormState("");
      setFormCity("");
    }
  }, [isAddModalOpen]);

  useEffect(() => {
    if (isModalOpen && currentRow) {
      // Set form fields when edit modal opens with current row data
      setFormCountry(currentRow.country_id?.toString() || "");
      setFormState(currentRow.state_id?.toString() || "");
      setFormCity(currentRow.city_id?.toString() || "");
    }
  }, [isModalOpen, currentRow]);

  // Handle form field changes from DynamicForm
  const handleFormFieldChange = (fieldId: string, value: string) => {
    console.log(`📥 Parent received form field change: ${fieldId} = ${value}`);
    
    switch (fieldId) {
      case "country":
        setFormCountry(value);
        break;
      case "state":
        setFormState(value);
        break;
      case "city":
        setFormCity(value);
        break;
      default:
        break;
    }
  };

  const locationData = Array.isArray(filteredLocationData)
    ? filteredLocationData.map((item: any, index: number) => ({
        sno: index + 1,
        id: item.id,
        area: item.area,
        zone: item.zone,
        country: item.country_name,
        state: item.state_name,
        city: item.city_name,
        latitude: item.latitude,
        longitude: item.longitude,
        created_by: item.created_by,
        created_at: item.created_at,
        country_id: item.country_id,
        state_id: item.state_id,
        city_id: item.city_id,
        action: "View",
      }))
    : [];

  const handleSearch = () => {
    const filtered = rawLocationData.filter((item) => {
      const matchCountry = country ? item.country_id == country : true;
      const matchState = state ? item.state_id == state : true;
      const matchCity = city ? item.city_id == city : true;
      const matchArea = area 
        ? item.area?.toLowerCase().includes(area.toLowerCase())
        : true;

      const matchStatus =
        activeStatus && inactiveStatus
          ? true
          : activeStatus
          ? item.status == 1
          : inactiveStatus
          ? item.status == 0
          : true;

      return matchCountry && matchState && matchCity && matchArea && matchStatus;
    });

    setFilteredLocationData(filtered);
  };

  const handleFormSubmit = (formData: Record<string, any>, type: string) => {
    const {
      area,
      zone,
      country,
      state,
      city,
      latitude,
      longitude,
    } = formData;

    // Removed status field from payload
    const payload: any = {
      area: area,
      zone: zone,
      country_id: country,
      state_id: state,
      city_id: city,
      latitude: latitude,
      longitude: longitude,
    };

    if (type === "update") {
      payload.id = currentRow?.id;
      
      updateLocationMutate(payload, {
        onSuccess: (res) => {
          toast.success("Location updated successfully");
          refetchLocations();
          setIsModalOpen(false);
          setCurrentRow(null);
        },
        onError: () => {
          toast.error("Failed to update location");
        },
      });
    } else {
      addLocationMutate(payload, {
        onSuccess: (res) => {
          toast.success("Location added successfully");
          refetchLocations();
          setIsAddModalOpen(false);
          setCurrentRow(null);
        },
        onError: () => {
          toast.error("Failed to add location");
        },
      });
    }
  };

  const handleStatusChange = (status_id: number, id?: any) => {
    let location_ids = [id];
    
    if (!id) {
      if (selectedRowIds.length === 0) {
        toast.error("No rows selected");
        return;
      }
      location_ids = selectedRowIds;
    }

    if (status_id === 2) {
      deleteLocationMutate(
        {
          ids: location_ids,
        },
        {
          onSuccess: (data) => {
            toast.success("Location(s) deleted successfully");
            refetchLocations();
            setSelectedRowIds([]);
          },
          onError: (error) => {
            toast.error("Failed to delete location(s)");
          },
        }
      );
    } else {
      updateLocationMutate(
        {
          id: location_ids[0],
          status: status_id,
        },
        {
          onSuccess: (data) => {
            toast.success("Status updated successfully");
            refetchLocations();
            setSelectedRowIds([]);
          },
          onError: (error) => {
            toast.error("Failed to update status");
          },
        }
      );
    }
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = filteredLocationData.map((row) => row.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedRowIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const allRowsSelected =
    filteredLocationData?.length > 0 &&
    selectedRowIds?.length === filteredLocationData?.length;

  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={allRowsSelected}
        />
      ),
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRowIds.includes(row.id)}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "area", header: "Area", sortable: true },
    { key: "zone", header: "Zone", sortable: true },
    { key: "country", header: "Country", sortable: true },
    { key: "state", header: "State", sortable: true },
    { key: "city", header: "City", sortable: true },
    { key: "latitude", header: "Latitude", sortable: true },
    { key: "longitude", header: "Longitude", sortable: true },
    {
      key: "created_by",
      header: "Created By",
      sortable: true,
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => handleStatusChange(2, row.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  const countryOptions = Array.isArray(countryList)
    ? countryList.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
    : [];

  // Search filter options
  const stateOptions = Array.isArray(stateList)
    ? stateList.map((item: any) => ({
        label: item.name || item.state_name,
        value: item.id,
      }))
    : [];

  const cityOptions = Array.isArray(cityList)
    ? cityList.map((item: any) => ({
        label: item.name || item.city_name,
        value: item.id,
      }))
    : [];

  // Form options (for modals)
  const formStateOptions = Array.isArray(formStateList)
    ? formStateList.map((item: any) => ({
        label: item.name || item.state_name,
        value: item.id,
      }))
    : [];

  const formCityOptions = Array.isArray(formCityList)
    ? formCityList.map((item: any) => ({
        label: item.name || item.city_name,
        value: item.id,
      }))
    : [];

  // Debugging for options
  console.log("Country Options:", countryOptions);
  console.log("Search State Options:", stateOptions);
  console.log("Search City Options:", cityOptions);
  console.log("Form State Options:", formStateOptions);
  console.log("Form City Options:", formCityOptions);

  const formFields: any = [
    {
      id: "area",
      label: "Area",
      type: "text",
      placeholder: "Enter Area",
      required: true,
      defaultValue: currentRow?.area || "",
    },
    {
      id: "zone",
      label: "Zone",
      type: "text",
      placeholder: "Enter Zone",
      required: true,
      defaultValue: currentRow?.zone || "",
    },
    {
      id: "country",
      label: "Country",
      type: "select",
      placeholder: "Select Country",
      required: true,
      defaultValue: formCountry || currentRow?.country_id || "",
      options: countryOptions,
    },
    {
      id: "state",
      label: "State",
      type: "select",
      placeholder: "Select State",
      required: true,
      defaultValue: formState || currentRow?.state_id || "",
      options: formStateOptions,
      disabled: !formCountry
    },
    {
      id: "city",
      label: "City",
      type: "select",
      placeholder: "Select City",
      required: true,
      defaultValue: formCity || currentRow?.city_id || "",
      options: formCityOptions,
      disabled: !formState
    },
    {
      id: "latitude",
      label: "Latitude",
      type: "text",
      placeholder: "Enter Latitude",
      required: true,
      defaultValue: currentRow?.latitude || "",
    },
    {
      id: "longitude",
      label: "Longitude",
      type: "text",
      placeholder: "Enter Longitude",
      required: true,
      defaultValue: currentRow?.longitude || "",
    },
    // Removed status field
  ];

  // Custom form submit handler that captures the form data and calls our handler
  const handleDynamicFormSubmit = (formData: Record<string, any>) => {
    console.log("Form submitted with data:", formData);
    const type = currentRow ? "update" : "add";
    handleFormSubmit(formData, type);
  };

  const handleClear = () => {
    setCountry("");
    setState("");
    setCity("");
    setArea("");
    setActiveStatus(true);
    setInactiveStatus(false);
    setFilteredLocationData(rawLocationData);
  };

  return (
    <div className="w-full bg-white">
      <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        {/* Country */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Country</label>
          <select
            value={country}
            onChange={(e) => {
              console.log("Search country selected:", e.target.value);
              setCountry(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            <option value="">Select Country</option>
            {countryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="text-xs text-gray-500">
            {countryOptions.length} countries available
          </div>
        </div>

        {/* State */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">State</label>
          <select
            value={state}
            onChange={(e) => {
              console.log("Search state selected:", e.target.value);
              setState(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            disabled={!country}
          >
            <option value="">Select State</option>
            {stateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="text-xs text-gray-500">
            {stateOptions.length} states available
          </div>
        </div>

        {/* City */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">City</label>
          <select
            value={city}
            onChange={(e) => {
              console.log("Search city selected:", e.target.value);
              setCity(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            disabled={!state}
          >
            <option value="">Select City</option>
            {cityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="text-xs text-gray-500">
            {cityOptions.length} cities available
          </div>
        </div>

        {/* Area */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Area
          </label>
          <input
            type="text"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="Enter area"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          />
        </div>

        {/* Status - Removed from search filters as well */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex items-center gap-4">
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="active"
                  checked={activeStatus}
                  onChange={(e) => setActiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setActiveStatus(!activeStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    activeStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      activeStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="active"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* In-Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="inactive"
                  checked={inactiveStatus}
                  onChange={(e) => setInactiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setInactiveStatus(!inactiveStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    inactiveStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      inactiveStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="inactive"
                className="text-sm text-gray-700 cursor-pointer"
              >
                In-Active
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleSearch}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search
          </Button>

          <Button
            size="sm"
            onClick={handleClear}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Location
          </Button>

          <Button
            size="sm"
            onClick={() => handleStatusChange(1)}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Publish
          </Button>

          <Button
            size="sm"
            onClick={() => handleStatusChange(0)}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Unpublish
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={() => handleStatusChange(2)}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Delete
          </Button>
        </div>
        <BookingTable
          data={locationData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          itemsPerPage={5}
        />
      </div>

      {/* Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setFormCountry("");
              setFormState("");
              setFormCity("");
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Update Location
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setFormCountry("");
                    setFormState("");
                    setFormCity("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleDynamicFormSubmit}
                  onFieldChange={handleFormFieldChange}
                  submitText="Update Location"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsAddModalOpen(false);
              setFormCountry("");
              setFormState("");
              setFormCity("");
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add New Location
                </h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setFormCountry("");
                    setFormState("");
                    setFormCity("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields.map((field: any) => ({
                    ...field,
                    defaultValue: "",
                  }))}
                  onSubmit={handleDynamicFormSubmit}
                  onFieldChange={handleFormFieldChange}
                  submitText="Add Location"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLocation;